import { createSlice } from "@reduxjs/toolkit";
import { 
    getAllSenderEmails, 
    getSenderEmailByID, 
    createSenderEmail, 
    updateSenderEmail, 
    deleteSenderEmail, 
    getDefaultSenderEmail, 
    setDefaultSenderEmail 
} from "./thunk";

interface SenderEmailState {
    senderEmails: any[];
    selectedSenderEmail: any;
    defaultSenderEmail: any;
    loading: boolean;
    error: string | null;
}

const initialState: SenderEmailState = {
    senderEmails: [],
    selectedSenderEmail: null,
    defaultSenderEmail: null,
    loading: false,
    error: null
};

const senderEmailSlice = createSlice({
    name: "senderEmail",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSelectedSenderEmail: (state) => {
            state.selectedSenderEmail = null;
        }
    },
    extraReducers: (builder) => {
        // Get all sender emails
        builder.addCase(getAllSenderEmails.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getAllSenderEmails.fulfilled, (state, action) => {
            state.loading = false;
            state.senderEmails = action.payload.data || [];
        });
        builder.addCase(getAllSenderEmails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch sender emails";
        });

        // Get sender email by ID
        builder.addCase(getSenderEmailByID.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getSenderEmailByID.fulfilled, (state, action) => {
            state.loading = false;
            state.selectedSenderEmail = action.payload.data;
        });
        builder.addCase(getSenderEmailByID.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch sender email";
        });

        // Create sender email
        builder.addCase(createSenderEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createSenderEmail.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload.data) {
                state.senderEmails.unshift(action.payload.data);
            }
        });
        builder.addCase(createSenderEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to create sender email";
        });

        // Update sender email
        builder.addCase(updateSenderEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateSenderEmail.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload.data) {
                const index = state.senderEmails.findIndex(email => email.id === action.payload.data.id);
                if (index !== -1) {
                    state.senderEmails[index] = action.payload.data;
                }
                if (state.selectedSenderEmail?.id === action.payload.data.id) {
                    state.selectedSenderEmail = action.payload.data;
                }
            }
        });
        builder.addCase(updateSenderEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to update sender email";
        });

        // Delete sender email
        builder.addCase(deleteSenderEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteSenderEmail.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload.id) {
                state.senderEmails = state.senderEmails.filter(email => email.id !== action.payload.id);
                if (state.selectedSenderEmail?.id === action.payload.id) {
                    state.selectedSenderEmail = null;
                }
            }
        });
        builder.addCase(deleteSenderEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to delete sender email";
        });

        // Get default sender email
        builder.addCase(getDefaultSenderEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getDefaultSenderEmail.fulfilled, (state, action) => {
            state.loading = false;
            state.defaultSenderEmail = action.payload.data;
        });
        builder.addCase(getDefaultSenderEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to fetch default sender email";
        });

        // Set default sender email
        builder.addCase(setDefaultSenderEmail.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(setDefaultSenderEmail.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload.data) {
                // Update the default sender email
                state.defaultSenderEmail = action.payload.data;
                
                // Update the sender emails list to reflect the new default
                state.senderEmails = state.senderEmails.map(email => ({
                    ...email,
                    is_default: email.id === action.payload.data.id
                }));
            }
        });
        builder.addCase(setDefaultSenderEmail.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "Failed to set default sender email";
        });
    }
});

export const { clearError, clearSelectedSenderEmail } = senderEmailSlice.actions;
export default senderEmailSlice.reducer;
